/**
 * Procurement Planning Page
 * Shared weekly procurement planner for admins and caterers.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { WeekSelector } from '@/components/rsvp/WeekSelector';
import { ReportTable } from '@/components/reports/ReportTable';
import { useUIStore } from '@/stores/uiStore';
import { ApiError } from '@/lib/api';
import { QuantityProblemsDialog } from '@/components/procurement/QuantityProblemsDialog';
import {
  addManualProcurementItem,
  deleteProcurementItem,
  exportProcurementDraftXLSX,
  exportProcurementXLSX,
  fetchProcurement,
  generateProcurement,
  saveInventory,
  updateProcurementItem,
} from '@/lib/api/procurement';
import { fetchIngredientVendors } from '@/lib/api/ingredientVendors';
import { fetchMenus } from '@/lib/api/menus';
import { ImportDialog, applyImportPlan, formatLongDate, type ConfirmMeta, type ConfirmPlan } from '@/components/procurement/import';
import {
  formatDate,
  formatWeekRange,
  fromISODate,
  getCurrentWeekStart,
  getWeekRange,
  isSunday,
  nextWeek,
  toISODate,
} from '@/lib/utils/dateHelpers';
import type {
  BulkInventoryEntry,
  BulkInventoryResponse,
  Ingredient,
  IngredientVendor,
  Menu,
  ProcurementItem,
  ProcurementListResponse,
  QuantityProblem,
  RSVPSummaryItem,
} from '@/types/api';
import { QUANTITY_OUT_OF_RANGE } from '@/types/api';
import { AlertTriangle, Download, FileSpreadsheet, List, Pencil, RefreshCcw, ShoppingBasket } from 'lucide-react';
import {
  ProcurementGroupTable,
  applySharedInventoryDraft,
  buildCategoryGroups,
  buildDraftMap,
  buildDraftProcurementExportGroups,
  buildRowSaveOperations,
  buildVendorGroups,
  convertSharedInventoryToLineUnit,
  createDraft,
  getInventoryUnitMismatch,
  getServerInventory,
  getSharedInventoryUnit,
  getStaleInventory,
  maskStaleInventory,
  formatQuantity,
  formatTimestamp,
  getSharedInventoryRowIds,
  isDraftChanged,
  parseQuantityInput,
  reconcileDraftAfterSave,
  resetAutoSyncedDrafts,
  searchIngredients,
  simulateProcurementItems,
  useProcurementAutoSave,
} from '@/components/procurement';
import {
  applyStoredRsvpSimulation,
  buildRsvpOverrides,
  createStoredRsvpSimulation,
  parseRsvpInput,
  readStoredRsvpSimulation,
  writeStoredRsvpSimulation,
} from '@/components/procurement/rsvpOverride';
import type {
  ProcurementDisplayItem,
  ProcurementItemDraft,
  ProcurementViewMode,
} from '@/components/procurement';
import type { RsvpOverride } from '@/components/procurement/rsvpOverride';
import { RSVP_SIZES, RSVP_SIZE_LABELS, getUnitConversionKey, type RSVPSizeCode } from '@/types/units';

// ---------------------------------------------------------------------------
// RSVP Override Feature (temporary)
// Client-side RSVP simulation layer for what-if procurement analysis.
// Remove once real RSVP data flows through the system.
//
// Removal checklist (search "RSVP Override Feature" for marked sections):
//  Imports:  fetchMenus, fromISODate, isSunday, Menu, RSVPSummaryItem,
//            Input, Pencil, and the imports from '@/components/procurement/rsvpOverride'
//  Files:    delete src/components/procurement/rsvpOverride.ts
//            delete src/__tests__/components/procurement/rsvpOverride.test.ts
//            remove simulateProcurementItems / RsvpOverrideMap from
//            src/components/procurement/helpers.ts and index.ts
//  State:    menus, rsvpOverrides, menusLoading, rsvpEditMode, regenDiff,
//            actualRsvpOverrides, visibleRsvpOverrides, rsvpStorageHydratedRef
//  Fetching: replace loadMenus with restored loadReport (fetchRSVPReport);
//            revert useEffect deps accordingly
//  Effects:  remove the RSVP local-storage hydration/persistence effects
//  Derived:  reportSummary, reportMeta, simulatedProcurementList (restore from fetchRSVPReport)
//  Pipeline: revert allItems to use procurementList.items directly
//  Callbacks: resetRsvpOverrides, updateRsvpOverride
//  RegenDiff: regenDiff state, setRegenDiff calls, RegenDiffEntry/RegenDiffMap types
//            (ProcurementGroupTable + index.ts), regenDiffEntry prop threading
//  Core touches: handleWeekChange (setRsvpEditMode + setRegenDiff resets)
//               handleGenerate (snapshot/diff logic, setRegenDiff in catch)
//               handleSaveAll (setRegenDiff reset)
//  JSX:      "Weekly RSVP Report" card edit-mode branch + buttons
// ---------------------------------------------------------------------------

function RsvpCountInput({
  value,
  onCommit,
  className,
  'aria-label': ariaLabel,
}: {
  value: number;
  onCommit: (nextValue: number) => void;
  className?: string;
  'aria-label'?: string;
}) {
  const [localValue, setLocalValue] = useState(String(value));

  // Sync local text when the external value changes (e.g., reset)
  useEffect(() => {
    setLocalValue(String(value));
  }, [value]);

  return (
    <Input
      aria-label={ariaLabel}
      className={className}
      inputMode="numeric"
      value={localValue}
      onChange={(event) => setLocalValue(event.target.value)}
      onBlur={() => {
        const parsed = parseRsvpInput(localValue);
        setLocalValue(String(parsed));
        if (parsed !== value) onCommit(parsed);
      }}
    />
  );
}

export function ProcurementPlanning() {
  const { showToast } = useUIStore();
  const [weekStart, setWeekStart] = useState<Date>(() => nextWeek(getCurrentWeekStart()));
  const [viewMode, setViewMode] = useState<ProcurementViewMode>('vendor');
  const [allBreakdownsExpanded, setAllBreakdownsExpanded] = useState<boolean | null>(null);
  const [data, setData] = useState<ProcurementListResponse | null>(null);
  // --- RSVP Override Feature (temporary) — state ---
  const [menus, setMenus] = useState<Menu[]>([]);
  const [rsvpOverrides, setRsvpOverrides] = useState<Record<number, RsvpOverride>>({});
  // --- end RSVP Override Feature state ---
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [menusLoading, setMenusLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [rsvpEditMode, setRsvpEditMode] = useState(false);
  const [quantityProblems, setQuantityProblems] = useState<QuantityProblem[] | null>(null);
  const [regenDiff, setRegenDiff] = useState<Record<number, { prevCalc: string; prevPurchase: string }>>({});
  const [draftItems, setDraftItems] = useState<Record<number, ProcurementItemDraft>>({});
  const [localManualItems, setLocalManualItems] = useState<ProcurementItem[]>([]);
  const [nextLocalItemId, setNextLocalItemId] = useState(-1);
  const [ingredientVendorsCache, setIngredientVendorsCache] = useState<Map<number, IngredientVendor[]>>(new Map());

  const weekStartDate = useMemo(() => toISODate(weekStart), [weekStart]);
  const weekEndDate = useMemo(() => {
    const { end } = getWeekRange(weekStart);
    return toISODate(end);
  }, [weekStart]);
  const rsvpStorageHydratedRef = useRef(false);

  const loadProcurement = useCallback(async (): Promise<ProcurementListResponse | null> => {
    try {
      setLoading(true);
      const response = await fetchProcurement(weekStartDate);
      setData(response);
      setLoadFailed(false);
      return response;
    } catch (error) {
      setLoadFailed(true);
      showToast(
        error instanceof Error ? error.message : 'Failed to load procurement list',
        'error'
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [showToast, weekStartDate]);

  const loadMenus = useCallback(async () => {
    try {
      setMenusLoading(true);
      const response = await fetchMenus(weekStartDate, weekEndDate, 'PUBLISHED');
      const menuRows = response
        .filter((menu) => menu.type === 'MENU')
        .filter((menu) => !isSunday(fromISODate(menu.date)));
      const storedSimulation = readStoredRsvpSimulation(weekStartDate);

      setMenus(menuRows);
      setRsvpOverrides(applyStoredRsvpSimulation(menuRows, storedSimulation));
      setRsvpEditMode(storedSimulation?.editMode ?? false);
      rsvpStorageHydratedRef.current = true;
    } catch (error) {
      rsvpStorageHydratedRef.current = false;
      setMenus([]);
      setRsvpOverrides({});
      setRsvpEditMode(false);
      showToast(
        error instanceof Error ? error.message : 'Failed to load menu data',
        'error'
      );
    } finally {
      setMenusLoading(false);
    }
  }, [showToast, weekEndDate, weekStartDate]);

  useEffect(() => {
    void Promise.all([loadProcurement(), loadMenus()]);
  }, [loadMenus, loadProcurement]);

  const procurementList = data?.procurementList ?? null;
  const actualRsvpOverrides = useMemo(() => buildRsvpOverrides(menus), [menus]);
  const visibleRsvpOverrides = useMemo(
    () => (rsvpEditMode ? rsvpOverrides : actualRsvpOverrides),
    [actualRsvpOverrides, rsvpEditMode, rsvpOverrides]
  );

  useEffect(() => {
    rsvpStorageHydratedRef.current = false;
  }, [weekStartDate]);

  useEffect(() => {
    if (menusLoading || !rsvpStorageHydratedRef.current) return;

    writeStoredRsvpSimulation(
      weekStartDate,
      createStoredRsvpSimulation(actualRsvpOverrides, rsvpOverrides, rsvpEditMode)
    );
  }, [actualRsvpOverrides, menusLoading, rsvpEditMode, rsvpOverrides, weekStartDate]);

  // Rebuild draft state only when the list identity changes (new week) or when the
  // snapshot is regenerated (generatedAt changes). We intentionally do NOT depend on
  // updatedAt, which changes after every save and would wipe unsaved drafts.
  useEffect(() => {
    if (!procurementList) {
      setDraftItems({});
      setLocalManualItems([]);
      setNextLocalItemId(-1);
      setIngredientVendorsCache(new Map());
      return;
    }

    setDraftItems(buildDraftMap(maskStaleInventory(procurementList.items)));
    setLocalManualItems([]);
    setNextLocalItemId(-1);
    // Don't clear ingredientVendorsCache — vendor associations are independent of the procurement snapshot
  }, [procurementList?.id, procurementList?.generatedAt]);

  // --- RSVP Override Feature (temporary) — derived state & simulation layer ---
  // When removing: delete reportSummary, reportMeta, simulatedProcurementList,
  // and revert allItems to use procurementList.items directly. Restore
  // reportSummary/reportMeta from fetchRSVPReport server response.
  const reportSummary = useMemo<RSVPSummaryItem[]>(
    () => menus.map((menu) => {
      const override = visibleRsvpOverrides[menu.id];
      const counts = override ?? { S: 0, M: 0, L: 0 };
      return {
        date: menu.date,
        menuTitle: menu.title,
        menuId: menu.id,
        smallCount: counts.S,
        mediumCount: counts.M,
        largeCount: counts.L,
        totalRsvps: RSVP_SIZES.reduce((sum, size) => sum + counts[size], 0),
      };
    }),
    [menus, visibleRsvpOverrides]
  );

  const reportMeta = useMemo(
    () => ({
      totalSmall: reportSummary.reduce((sum, row) => sum + row.smallCount, 0),
      totalMedium: reportSummary.reduce((sum, row) => sum + row.mediumCount, 0),
      totalLarge: reportSummary.reduce((sum, row) => sum + row.largeCount, 0),
      totalRsvps: reportSummary.reduce((sum, row) => sum + row.totalRsvps, 0),
    }),
    [reportSummary]
  );

  const simulatedProcurementList = useMemo(() => {
    if (!procurementList) return null;
    return {
      ...procurementList,
      rsvpCountS: reportMeta.totalSmall,
      rsvpCountM: reportMeta.totalMedium,
      rsvpCountL: reportMeta.totalLarge,
      items: simulateProcurementItems(procurementList.items, rsvpOverrides),
    };
  }, [procurementList, reportMeta.totalLarge, reportMeta.totalMedium, reportMeta.totalSmall, rsvpOverrides]);

  // Display pipeline: use server snapshot by default, switch to simulated
  // items only when the user is actively in RSVP edit mode. This prevents
  // the UI from silently diverging from DB on page load.
  // When removing: revert to procurementList.items directly.
  const allItems = useMemo(
    () => {
      const source = rsvpEditMode ? simulatedProcurementList : procurementList;
      return source ? [...maskStaleInventory(source.items, procurementList?.items ?? source.items), ...localManualItems] : [];
    },
    [localManualItems, simulatedProcurementList, procurementList, rsvpEditMode]
  );
  // Shared on-hand netting is decided from the generated lines, not the simulated
  // ones: a line whose simulated RSVPs drop to 0 disappears from the display but
  // still owns the on-hand on the server.
  const nettingItems = useMemo(
    () => (procurementList ? [...maskStaleInventory(procurementList.items), ...localManualItems] : []),
    [localManualItems, procurementList]
  );
  // --- end RSVP Override Feature derived state ---

  // Sync draft purchaseQuantity from simulated values when the user hasn't
  // manually edited it. Uses autoPurchaseQuantity to track the last system-
  // computed value, so repeated RSVP changes keep syncing correctly while
  // true manual edits are preserved. When removing RSVP Override Feature:
  // delete this effect.
  useEffect(() => {
    if (!rsvpEditMode || !simulatedProcurementList) return;
    setDraftItems((current) => {
      let changed = false;
      const next = { ...current };
      for (const simItem of simulatedProcurementList.items) {
        const draft = current[simItem.id];
        if (!draft) continue;
        const prevAuto = draft.autoPurchaseQuantity ?? draft.purchaseQuantity;
        const nextAuto = formatQuantity(simItem.purchaseQuantity);
        if (prevAuto !== nextAuto) {
          next[simItem.id] = draft.purchaseQuantity === prevAuto
            ? { ...draft, purchaseQuantity: nextAuto, autoPurchaseQuantity: nextAuto }
            : { ...draft, autoPurchaseQuantity: nextAuto };
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [rsvpEditMode, simulatedProcurementList]);

  const displayItems = useMemo<ProcurementDisplayItem[]>(
    () => allItems.map((item) => {
      const draft = draftItems[item.id] ?? createDraft(item);
      return {
        item,
        draft,
        isLocalOnly: item.id < 0,
        inventoryTrackedIn: getInventoryUnitMismatch(nettingItems, item),
        inventoryInLineUnit: convertSharedInventoryToLineUnit(nettingItems, item, draft.inventoryQuantity, { typed: draft.inventoryQuantity !== getServerInventory(item) }) ?? '',
        inventoryUnit: getSharedInventoryUnit(nettingItems, item.ingredientId),
        inventoryStale: getStaleInventory(procurementList?.items ?? [], item),
      };
    }),
    [allItems, draftItems, nettingItems, procurementList]
  );

  const groupedItems = useMemo(
    () => (viewMode === 'vendor' ? buildVendorGroups(displayItems) : buildCategoryGroups(displayItems)),
    [displayItems, viewMode]
  );

  const pendingChangeCount = useMemo(
    () => displayItems.filter(({ item, draft, isLocalOnly }) => isLocalOnly || isDraftChanged(item, draft)).length,
    [displayItems]
  );

  const hasPendingChanges = pendingChangeCount > 0;

  // --- Auto-save on row exit ---
  // Rows save themselves (debounced, or immediately when focus leaves the row).
  // On success the callbacks below patch state in place instead of reloading
  // the whole list, so drafts on other rows are untouched.
  const handleRowItemSaved = useCallback((previousId: number, saved: ProcurementItem) => {
    setData((current) => {
      const list = current?.procurementList;
      if (!current || !list) return current;
      const previous = list.items.find((item) => item.id === previousId);
      // Item responses carry no inventory join; keep the value already loaded.
      const nextItem: ProcurementItem = {
        ...saved,
        inventoryQuantity: saved.inventoryQuantity ?? previous?.inventoryQuantity ?? null,
        inventoryUnit: saved.inventoryUnit ?? previous?.inventoryUnit ?? null,
      };
      const items = previous
        ? list.items.map((item) => (item.id === previousId ? nextItem : item))
        : [...list.items, nextItem];
      return { ...current, procurementList: { ...list, items, updatedAt: saved.updatedAt } };
    });
    if (previousId < 0) {
      setLocalManualItems((current) => current.filter((item) => item.id !== previousId));
    }
    setDraftItems((current) => {
      const { [previousId]: previousDraft, ...rest } = current;
      // The user may have kept typing while the request was in flight: keep the
      // current draft and only normalize the fields the server now agrees with.
      return {
        ...rest,
        [saved.id]: reconcileDraftAfterSave(previousDraft ?? createDraft(saved), saved),
      };
    });
  }, []);

  const handleRowInventorySaved = useCallback((entries: BulkInventoryResponse['entries'], savedIngredientIds: number[]) => {
    // The bulk endpoint returns every inventory row for the list, but it is only
    // authoritative for the ingredient(s) this request sent: another row's save
    // may be in flight, so an unrelated ingredient absent from this snapshot
    // must not be blanked. For a sent ingredient, absent means cleared.
    const savedIds = new Set(savedIngredientIds);
    const byIngredient = new Map(entries.map((entry) => [entry.ingredientId, entry.quantityOnHand]));
    const unitByIngredient = new Map(entries.map((entry) => [entry.ingredientId, entry.unit]));
    setData((current) => {
      const list = current?.procurementList;
      if (!current || !list) return current;
      const items = list.items.map((item) => (
        item.sourceType === 'MANUAL' || !savedIds.has(item.ingredientId)
          ? item
          : { ...item, inventoryQuantity: byIngredient.get(item.ingredientId) ?? null, inventoryUnit: unitByIngredient.get(item.ingredientId) ?? null }
      ));
      return { ...current, procurementList: { ...list, items } };
    });
    // Normalize drafts that now equal the server value (e.g. "3.5" saved as 4)
    // so the row stops counting as unsaved; leave genuinely different drafts alone.
    setDraftItems((current) => {
      let changed = false;
      const next = { ...current };
      for (const { item } of displayItems) {
        if (item.sourceType === 'MANUAL' || !savedIds.has(item.ingredientId)) continue;
        const draft = current[item.id];
        if (!draft) continue;
        const serverValue = byIngredient.get(item.ingredientId) ?? null;
        const normalizedServer = serverValue ? formatQuantity(serverValue) : '';
        if (draft.inventoryQuantity === normalizedServer) continue;
        const draftParsed = draft.inventoryQuantity === '' ? null : parseQuantityInput(draft.inventoryQuantity);
        const serverParsed = serverValue === null ? null : Number(serverValue);
        if ((draftParsed ?? 0) === (serverParsed ?? 0)) {
          next[item.id] = { ...draft, inventoryQuantity: normalizedServer };
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [displayItems]);

  const handleRowManualDeleted = useCallback((itemId: number) => {
    setData((current) => {
      const list = current?.procurementList;
      if (!current || !list) return current;
      return { ...current, procurementList: { ...list, items: list.items.filter((item) => item.id !== itemId) } };
    });
    setDraftItems((current) => {
      if (!(itemId in current)) return current;
      const { [itemId]: _, ...rest } = current;
      return rest;
    });
  }, []);

  const autoSaveEnabled = !rsvpEditMode && !saving && !generating;
  const {
    rowStatus: rowSaveStatus,
    scheduleRowSave,
    flushRowSave,
    flushAll,
  } = useProcurementAutoSave({
    procurementListId: procurementList?.id ?? null,
    displayItems,
    enabled: autoSaveEnabled,
    onItemSaved: handleRowItemSaved,
    onInventorySaved: handleRowInventorySaved,
    onManualDeleted: handleRowManualDeleted,
  });

  // Always the items of the latest render, for code that resumes after an
  // await and must not read the list captured when the handler was created.
  const displayItemsRef = useRef(displayItems);
  displayItemsRef.current = displayItems;

  /**
   * Flush pending auto-saves, then report the rows that still differ from the
   * server. Computed from the latest render rather than by excluding the saved
   * ids: a saved row's draft has been reconciled and no longer differs, while a
   * row edited during the flush (even a "saved" one) still does and must count.
   */
  const flushThenGetRemaining = useCallback(async () => {
    const result = await flushAll();
    if (result.saved.length > 0) {
      // Let React commit what the save callbacks patched before re-reading.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    const remaining = displayItemsRef.current.filter(({ item, draft, isLocalOnly }) => (
      isLocalOnly || isDraftChanged(item, draft)
    ));
    return { ...result, remaining };
  }, [flushAll]);

  // Flush pending auto-saves first; only ask about discarding what could not be saved.
  const flushThenConfirmDiscard = useCallback(async (message: string) => {
    const { remaining } = await flushThenGetRemaining();
    if (remaining.length === 0 || typeof window === 'undefined') {
      return true;
    }
    return window.confirm(message);
  }, [flushThenGetRemaining]);
  // --- end auto-save ---

  const handleWeekChange = useCallback(async (nextValue: Date) => {
    if (!(await flushThenConfirmDiscard('Discard unsaved procurement changes and switch weeks?'))) {
      return;
    }
    setRsvpEditMode(false);
    setRegenDiff({});
    setWeekStart(nextValue);
  }, [flushThenConfirmDiscard]);

  // A quantity refusal opens the fix-first dialog; any other failure toasts.
  const showGenerateError = useCallback((error: unknown, fallback: string) => {
    if (error instanceof ApiError && error.code === QUANTITY_OUT_OF_RANGE) {
      setQuantityProblems((error.body as { problems?: QuantityProblem[] } | undefined)?.problems ?? []);
    } else {
      showToast(error instanceof Error ? error.message : fallback, 'error');
    }
  }, [showToast]);

  const handleGenerate = useCallback(async () => {
    if (!(await flushThenConfirmDiscard('Discard unsaved procurement changes and regenerate this week?'))) {
      return;
    }

    // Snapshot current items so we can diff after regeneration
    const prevItems = new Map(
      (procurementList?.items ?? []).map((item) => [item.id, {
        calculatedQuantity: item.calculatedQuantity,
        purchaseQuantity: item.purchaseQuantity,
        ingredientId: item.ingredientId,
        unit: item.unit,
      }])
    );

    try {
      setGenerating(true);
      const response = await generateProcurement({ weekStartDate });
      setData(response);

      // Build diff: regenerate keeps line ids, so match by id first; a recreated line
      // falls back to ingredient + unit group, the key the server matches lines by.
      const prevByLine = new Map<string, { calc: string; purchase: string }>();
      for (const item of prevItems.values()) {
        prevByLine.set(`${item.ingredientId}:${getUnitConversionKey(item.unit)}`, {
          calc: item.calculatedQuantity,
          purchase: item.purchaseQuantity,
        });
      }

      const nextDiff: Record<number, { prevCalc: string; prevPurchase: string }> = {};
      for (const item of response.procurementList?.items ?? []) {
        const prevById = prevItems.get(item.id);
        const prev = prevById
          ? { calc: prevById.calculatedQuantity, purchase: prevById.purchaseQuantity }
          : prevByLine.get(`${item.ingredientId}:${getUnitConversionKey(item.unit)}`);
        if (prev && (prev.calc !== item.calculatedQuantity || prev.purchase !== item.purchaseQuantity)) {
          nextDiff[item.id] = { prevCalc: prev.calc, prevPurchase: prev.purchase };
        }
      }

      setRegenDiff(nextDiff);
      const changedCount = Object.keys(nextDiff).length;
      // Re-fetch menus so the RSVP report reflects current data
      void loadMenus();
      showToast(
        response.procurementList
          ? `Procurement list regenerated${changedCount > 0 ? ` — ${changedCount} item${changedCount > 1 ? 's' : ''} recalculated` : ''}`
          : 'Procurement list generated',
        'success'
      );
    } catch (error) {
      setRegenDiff({});
      showGenerateError(error, 'Failed to generate procurement list');
    } finally {
      setGenerating(false);
    }
  }, [flushThenConfirmDiscard, loadMenus, procurementList?.items, showGenerateError, showToast, weekStartDate]);

  const resetDrafts = useCallback(() => {
    if (!procurementList) return;
    setDraftItems(buildDraftMap(maskStaleInventory(procurementList.items)));
    setLocalManualItems([]);
    setNextLocalItemId(-1);
    showToast('Unsaved procurement changes cleared', 'success');
  }, [procurementList, showToast]);

  // --- RSVP Override Feature (temporary) — callbacks ---
  const toggleRsvpEditMode = useCallback(() => {
    if (!rsvpEditMode) {
      if (typeof window !== 'undefined' && !window.confirm(
        'This enters simulation mode. RSVP changes here are saved locally in this browser for this week and do not update the database. Use this to preview how different RSVP counts affect procurement quantities.\n\nContinue?'
      )) {
        return;
      }

      setRsvpEditMode(true);
      return;
    }

    // Reset auto-synced purchaseQuantity back to server baseline
    if (procurementList) {
      setDraftItems((drafts) => resetAutoSyncedDrafts(drafts, procurementList.items));
    }
    setRsvpEditMode(false);
  }, [procurementList, rsvpEditMode]);

  const resetRsvpOverrides = useCallback(() => {
    setRsvpOverrides(actualRsvpOverrides);
    showToast('RSVP assumptions reset', 'success');
  }, [actualRsvpOverrides, showToast]);

  const updateRsvpOverride = useCallback(
    (menuId: number, field: RSVPSizeCode, nextValue: number) => {
      setRsvpOverrides((current) => {
        const existing = current[menuId] ?? { S: 0, M: 0, L: 0, isAssumed: false };
        if (existing[field] === nextValue) return current;
        return {
          ...current,
          [menuId]: { ...existing, [field]: nextValue, isAssumed: false },
        };
      });
    },
    []
  );
  // --- end RSVP Override Feature callbacks ---

  const updateDraft = useCallback((itemId: number, patch: Partial<ProcurementItemDraft>) => {
    setDraftItems((current) => {
      const existing = current[itemId];
      if (!existing) return current;
      return {
        ...current,
        [itemId]: {
          ...existing,
          ...patch,
        },
      };
    });
    // Clear regeneration diff for this item once the user edits it
    setRegenDiff((current) => {
      if (!current[itemId]) return current;
      const { [itemId]: _, ...rest } = current;
      return rest;
    });
    scheduleRowSave(itemId);
  }, [scheduleRowSave]);

  const toggleExcluded = useCallback((itemId: number) => {
    setDraftItems((current) => {
      const existing = current[itemId];
      if (!existing) return current;
      return {
        ...current,
        [itemId]: {
          ...existing,
          isExcluded: !existing.isExcluded,
        },
      };
    });
    // Discrete action: save right away rather than waiting for the debounce
    flushRowSave(itemId);
  }, [flushRowSave]);

  const updateInventory = useCallback((itemId: number, inventoryValue: string) => {
    // Use allItems (which includes simulated quantities) so the suggested
    // purchase matches the visible calculatedQuantity on screen.
    const item = allItems.find((i) => i.id === itemId);
    if (!item || item.sourceType === 'MANUAL') {
      setDraftItems((current) => {
        const existing = current[itemId];
        if (!existing) return current;
        return { ...current, [itemId]: { ...existing, inventoryQuantity: inventoryValue } };
      });
      scheduleRowSave(itemId);
      return;
    }
    // Inventory is one record per ingredient: a RECIPE row and a STAPLE row of
    // the same ingredient share it, so patch every sibling and recompute each
    // purchase (recipe need consumes the stock first; the staple nets the rest).
    setDraftItems((current) => applySharedInventoryDraft(current, allItems, item.ingredientId, inventoryValue, nettingItems));
    // Each sibling's purchase changed, so each saves itself (the shared
    // inventory entry is re-sent with the same value, which is idempotent).
    for (const rowId of getSharedInventoryRowIds(allItems, item.ingredientId)) {
      scheduleRowSave(rowId);
    }
  }, [allItems, scheduleRowSave]);

  const ingredientVendorsCacheRef = useRef(ingredientVendorsCache);
  ingredientVendorsCacheRef.current = ingredientVendorsCache;
  const inFlightVendorFetches = useRef(new Set<number>());

  const fetchAndCacheIngredientVendors = useCallback(async (ingredientId: number) => {
    if (ingredientVendorsCacheRef.current.has(ingredientId)) return;
    if (inFlightVendorFetches.current.has(ingredientId)) return;
    inFlightVendorFetches.current.add(ingredientId);
    try {
      const vendors = await fetchIngredientVendors(ingredientId);
      setIngredientVendorsCache((current) => {
        const next = new Map(current);
        next.set(ingredientId, vendors);
        return next;
      });
    } finally {
      inFlightVendorFetches.current.delete(ingredientId);
    }
  }, []);

  const handleVendorChange = useCallback((itemId: number, vendorId: number | null, vendorName: string | null) => {
    setDraftItems((current) => {
      const existing = current[itemId];
      if (!existing) return current;
      return {
        ...current,
        [itemId]: {
          ...existing,
          vendorId,
          vendorNameSnapshot: vendorName,
        },
      };
    });
    // Discrete action: save right away rather than waiting for the debounce
    flushRowSave(itemId);
  }, [flushRowSave]);

  const handleAddManualItem = useCallback((ingredient: Ingredient) => {
    if (!procurementList) return;

    const tempId = nextLocalItemId;
    const timestamp = new Date().toISOString();
    const nextItem: ProcurementItem = {
      id: tempId,
      procurementListId: procurementList.id,
      sourceType: 'MANUAL',
      ingredientId: ingredient.id,
      displayName: ingredient.name,
      unit: ingredient.defaultUnit,
      calculatedQuantity: '0',
      purchaseQuantity: '1',
      vendorId: null,
      vendorNameSnapshot: null,
      notes: null,
      isExcluded: false,
      displayOrder: procurementList.items.length + localManualItems.length,
      calculationBreakdown: [],
      inventoryQuantity: null,
      createdAt: timestamp,
      updatedAt: timestamp,
      ingredient,
    };

    setLocalManualItems((current) => [...current, nextItem]);
    setDraftItems((current) => ({
      ...current,
      [tempId]: createDraft(nextItem),
    }));
    setNextLocalItemId((current) => current - 1);
    showToast(`Added ${ingredient.name} to unsaved changes`, 'success');
  }, [
    localManualItems.length,
    nextLocalItemId,
    procurementList,
    showToast,
  ]);

  const handleSaveAll = useCallback(async () => {
    if (!procurementList) return;

    // Let pending auto-saves finish first; the bulk path below is the retry
    // for rows that failed (or could not be auto-saved).
    const { saved: autoSavedRowIds, remaining } = await flushThenGetRemaining();
    const autoSavedCount = autoSavedRowIds.length;

    const operations: Promise<unknown>[] = [];
    let appliedChanges = 0;

    try {
      setSaving(true);

      const inventoryEntries: BulkInventoryEntry[] = [];
      for (const { item, draft, isLocalOnly } of remaining) {
        const ops = buildRowSaveOperations(item, draft, isLocalOnly);
        if (ops.error) {
          throw new Error(ops.error);
        }
        if (ops.inventoryEntry) {
          inventoryEntries.push(ops.inventoryEntry);
        }

        if (isLocalOnly) {
          if (ops.createManual) {
            operations.push(addManualProcurementItem(procurementList.id, ops.createManual));
          }
          // Excluded local-only rows are simply dropped on reload
          appliedChanges += 1;
          continue;
        }

        if (ops.deleteManual) {
          operations.push(deleteProcurementItem(item.id));
          appliedChanges += 1;
          continue;
        }

        if (ops.itemPayload) {
          operations.push(updateProcurementItem(item.id, ops.itemPayload));
          appliedChanges += 1;
        }
      }

      if (inventoryEntries.length > 0) {
        operations.push(saveInventory(procurementList.id, inventoryEntries));
        appliedChanges += 1;
      }

      if (appliedChanges === 0) {
        showToast(
          autoSavedCount > 0
            ? `Saved ${autoSavedCount} procurement change${autoSavedCount === 1 ? '' : 's'}`
            : 'No procurement changes to save',
          'success'
        );
        return;
      }

      const results = await Promise.allSettled(operations);
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');

      // Always reload from server to reconcile state after partial or full save.
      setLocalManualItems([]);
      setRegenDiff({});
      await loadProcurement();

      if (failures.length > 0) {
        const savedCount = results.length - failures.length;
        showToast(
          `${savedCount} of ${results.length} changes saved. ${failures.length} failed — review and retry.`,
          'error'
        );
      } else {
        const totalSaved = appliedChanges + autoSavedCount;
        showToast(`Saved ${totalSaved} procurement change${totalSaved === 1 ? '' : 's'}`, 'success');
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to save procurement changes',
        'error'
      );
    } finally {
      setSaving(false);
    }
  }, [flushThenGetRemaining, loadProcurement, procurementList, showToast]);

  const importExistingItems = useMemo(() => (procurementList?.items ?? []).map((item) => ({
    id: item.id,
    ingredientId: item.ingredientId,
    sourceType: item.sourceType,
    purchaseQuantity: item.purchaseQuantity,
    unit: item.unit,
    isExcluded: item.isExcluded,
    vendorId: item.vendorId,
    notes: item.notes,
  })), [procurementList?.items]);

  // A week without a list is generated first so the dialog can see its recipe and staple items.
  const handleOpenImport = useCallback(async () => {
    if (loading) return;
    if (loadFailed) {
      showToast('Couldn\'t load this week\'s list — reload the page and try again', 'error');
      return;
    }
    if (procurementList) {
      if (!(await flushThenConfirmDiscard('Discard unsaved procurement changes and import from a sheet?'))) return;
      setImportOpen(true);
      return;
    }
    try {
      setGenerating(true);
      const generated = await generateProcurement({ weekStartDate });
      setData(generated);
      setImportOpen(true);
    } catch (error) {
      showGenerateError(error, 'Failed to generate this week\'s list');
    } finally {
      setGenerating(false);
    }
  }, [flushThenConfirmDiscard, loading, loadFailed, procurementList, showGenerateError, showToast, weekStartDate]);

  const handleImportConfirm = useCallback(async (plan: ConfirmPlan, meta: ConfirmMeta) => {
    // Let pending auto-saves land first so the reload below does not drop them.
    await flushAll();

    const listId = procurementList?.id ?? null;
    if (listId === null) throw new Error('Could not find this week\'s procurement list');

    const outcome = await applyImportPlan(plan, listId, undefined, importExistingItems);

    setLocalManualItems([]);
    setRegenDiff({});
    const reloaded = await loadProcurement();
    // Drafts are only rebuilt on a new list or regenerate: re-seed touched and new rows from the saved values.
    const reloadedItems = reloaded?.procurementList?.items;
    if (reloadedItems) {
      const touched = new Set(outcome.updatedItemIds);
      const manualIngredients = new Set(outcome.manualIngredientIds);
      for (const item of reloadedItems) {
        if (item.sourceType === 'MANUAL' && manualIngredients.has(item.ingredientId)) touched.add(item.id);
      }
      setDraftItems((current) => Object.fromEntries(reloadedItems.map((item) => [
        item.id,
        touched.has(item.id) || !current[item.id] ? createDraft(item) : current[item.id]!,
      ])));
    }

    const { sent, failed, createFailures, unitFailures, vendorFailures, exclusionFailures, reuseConflicts } = outcome;
    const vendorNote = `Couldn't save the vendor for ${vendorFailures} new ingredient${vendorFailures === 1 ? '' : 's'}`;
    const otherNotes = [
      createFailures > 0 ? `${createFailures} row${createFailures === 1 ? '' : 's'} couldn't be created` : null,
      unitFailures > 0 ? `${unitFailures} row${unitFailures === 1 ? '' : 's'} matched an existing ingredient in a unit the sheet amount can't convert to` : null,
      exclusionFailures > 0 ? `Couldn't strike out ${exclusionFailures} recipe row${exclusionFailures === 1 ? '' : 's'}` : null,
      reuseConflicts > 0 ? `${reuseConflicts} new-ingredient row${reuseConflicts === 1 ? '' : 's'} matched an ingredient already on this week's list and ${reuseConflicts === 1 ? 'was' : 'were'} skipped — pick it in the review instead of Create` : null,
    ];

    if (failed > 0) {
      const tail = [vendorFailures > 0 ? vendorNote : null, ...otherNotes].filter(Boolean).map((note) => ` ${note}.`).join('');
      showToast(`${sent - failed} of ${sent} items added. ${failed} failed — check the list and retry.${tail}`, 'error');
    } else {
      const added = `Added ${sent} item${sent === 1 ? '' : 's'} to this week`;
      const leftOut = meta.excludedCount > 0 ? `; ${meta.excludedCount} row${meta.excludedCount === 1 ? '' : 's'} left out` : '';
      const notes = [vendorFailures > 0 ? `${vendorNote} — set it in the ingredient library` : null, ...otherNotes].filter(Boolean);
      if (notes.length > 0) showToast(`${added}${leftOut}. ${notes.join('. ')}.`, 'error');
      else showToast(`${added}${leftOut}`, 'success');
    }
  }, [flushAll, importExistingItems, loadProcurement, procurementList?.id, showToast]);

  const handleExport = useCallback(async () => {
    if (!procurementList || loading) return;

    if (!rsvpEditMode && hasPendingChanges) {
      showToast('Save procurement changes before exporting', 'info');
      return;
    }

    try {
      setExporting(true);
      if (rsvpEditMode) {
        await exportProcurementDraftXLSX(
          buildDraftProcurementExportGroups(displayItems),
          `procurement-list-${weekStartDate}-to-${weekEndDate}.xlsx`,
          procurementList.warnings.map((warning) => warning.message)
        );
      } else {
        await exportProcurementXLSX(procurementList.id);
      }
      showToast(
        rsvpEditMode ? 'Procurement simulation exported successfully' : 'Procurement list exported successfully',
        'success'
      );
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to export procurement list',
        'error'
      );
    } finally {
      setExporting(false);
    }
  }, [
    displayItems,
    hasPendingChanges,
    loading,
    procurementList,
    rsvpEditMode,
    showToast,
    weekEndDate,
    weekStartDate,
  ]);

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <ShoppingBasket className="h-6 w-6 text-muted-foreground" />
            <h1 className="text-2xl font-bold">Procurement Planning</h1>
          </div>
          <p className="text-muted-foreground">
            Build and maintain the weekly shopping list from current menus and RSVP counts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {procurementList && (
            <>
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting || loading || saving || generating}
              >
                {exporting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Export XLSX
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={resetDrafts}
                disabled={!hasPendingChanges || saving || generating}
              >
                Reset changes
              </Button>
              <Button
                onClick={handleSaveAll}
                disabled={!hasPendingChanges || saving || generating}
              >
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handleOpenImport} disabled={generating || saving || loading}>
            <FileSpreadsheet className="h-4 w-4" />
            Import from sheet
          </Button>
          <Button onClick={handleGenerate} disabled={generating || saving}>
            {generating ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                Working...
              </>
            ) : (
              <>
                <RefreshCcw className="h-4 w-4" />
                {procurementList ? 'Regenerate Snapshot' : 'Generate Snapshot'}
              </>
            )}
          </Button>
        </div>
      </div>

      <WeekSelector
        weekStart={weekStart}
        onWeekChange={handleWeekChange}
        disabled={loading || generating || saving}
      />

      <Card className="gap-4">
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <CardTitle>{formatWeekRange(weekStart)}</CardTitle>
              <CardDescription>
                {data?.cutoffPassed
                  ? 'Cutoff has passed for this week. Snapshot generation uses the final RSVP counts available now.'
                  : 'Cutoff has not passed yet. Snapshot generation uses the current live RSVP counts.'}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant={data?.cutoffPassed ? 'secondary' : 'default'}>
                {data?.cutoffPassed ? 'Cutoff passed' : 'Cutoff open'}
              </Badge>
              <Badge variant="outline">S {procurementList?.rsvpCountS ?? 0}</Badge>
              <Badge variant="outline">M {procurementList?.rsvpCountM ?? 0}</Badge>
              <Badge variant="outline">L {procurementList?.rsvpCountL ?? 0}</Badge>
              {hasPendingChanges && (
                <Badge variant="secondary">{pendingChangeCount} unsaved</Badge>
              )}
            </div>
          </div>
          {procurementList && (
            <div className="flex flex-col gap-1 text-sm text-muted-foreground lg:flex-row lg:items-center lg:gap-4">
              <span>Generated {formatTimestamp(procurementList.generatedAt)}</span>
              <span>Updated {formatTimestamp(procurementList.updatedAt)}</span>
            </div>
          )}
        </CardHeader>
      </Card>

      <Card className="gap-4">
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <CardTitle>Weekly RSVP Report</CardTitle>
              <CardDescription>
                RSVP counts for the selected week. Edit mode applies client-side assumptions.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button
                variant={rsvpEditMode ? 'default' : 'outline'}
                size="sm"
                onClick={toggleRsvpEditMode}
                disabled={menusLoading}
              >
                <Pencil className="h-4 w-4" />
                {rsvpEditMode ? 'Done editing RSVPs' : 'Edit RSVP inputs'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={resetRsvpOverrides}
                disabled={menusLoading || menus.length === 0}
              >
                Reset assumptions
              </Button>
              <Badge variant="outline">S {reportMeta.totalSmall}</Badge>
              <Badge variant="outline">M {reportMeta.totalMedium}</Badge>
              <Badge variant="outline">L {reportMeta.totalLarge}</Badge>
              <Badge variant="secondary">{reportMeta.totalRsvps} total</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {menusLoading ? (
            <div className="flex items-center justify-center py-10" role="status" aria-label="Loading RSVP report">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : reportSummary.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No published menus for this week.</p>
          ) : rsvpEditMode ? (
            <div className="space-y-3">
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Menu</th>
                      {RSVP_SIZES.map((size) => (
                        <th key={size} className="w-24 px-4 py-3 text-right text-sm font-semibold">{size} Count</th>
                      ))}
                      <th className="px-4 py-3 text-right text-sm font-semibold"># of Families</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportSummary.map((row) => {
                      const override = rsvpOverrides[row.menuId];

                      return (
                        <tr key={row.menuId} className="border-b">
                          <td className="px-4 py-[9px] text-sm">{formatDate(fromISODate(row.date), 'EEE, MMM d')}</td>
                          <td className="px-4 py-[9px] text-sm font-medium">{row.menuTitle}</td>
                          {RSVP_SIZES.map((size) => (
                            <td key={size} className="px-4 py-[9px] text-right">
                              <RsvpCountInput
                                aria-label={`${RSVP_SIZE_LABELS[size]} count for ${row.menuTitle}`}
                                className="ml-auto h-7 w-14 text-right text-sm"
                                value={override?.[size] ?? 0}
                                onCommit={(v) => updateRsvpOverride(row.menuId, size, v)}
                              />
                            </td>
                          ))}
                          <td className="px-4 py-[9px] text-right text-sm">
                            <Badge variant="secondary">
                              {RSVP_SIZES.reduce((sum, size) => sum + (override?.[size] ?? 0), 0)}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="space-y-3 md:hidden">
                {reportSummary.map((row) => {
                  const override = rsvpOverrides[row.menuId];
                  return (
                    <div key={row.menuId} className="space-y-2 rounded-lg border p-3">
                      <div>
                        <p className="text-sm font-semibold">{row.menuTitle}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(fromISODate(row.date), 'EEEE, MMM d')}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {RSVP_SIZES.map((size) => (
                          <RsvpCountInput
                            key={size}
                            aria-label={`${RSVP_SIZE_LABELS[size]} count for ${row.menuTitle}`}
                            value={override?.[size] ?? 0}
                            onCommit={(v) => updateRsvpOverride(row.menuId, size, v)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-sm text-muted-foreground">
                These RSVP inputs are saved locally in this browser for this week and do not write to backend RSVP data.
              </p>
            </div>
          ) : (
            <ReportTable
              view="summary"
              summaryData={reportSummary}
              detailsData={[]}
            />
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16" role="status" aria-label="Loading procurement list">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : !procurementList ? (
        <Card className="p-12 text-center">
          <CardHeader className="px-0">
            <CardTitle>No procurement snapshot for this week</CardTitle>
            <CardDescription>
              Generate the weekly list to start adjusting quantities, excluding items, or adding manual items.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            <Button onClick={handleGenerate} disabled={generating}>
              Generate Snapshot
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {procurementList.warnings.length > 0 && (
            <Card className="gap-4 border-amber-200 bg-amber-50/50">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <CardTitle className="text-base">Generation warnings</CardTitle>
                </div>
                <CardDescription>
                  Check these menus and ingredients. Some rows were skipped and some need a fix in the Kitchen Library.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-amber-900">
                  {procurementList.warnings.map((warning, index) => (
                    <li key={`${warning.code}-${warning.menuId ?? 'x'}-${warning.ingredientId ?? 'x'}-${index}`}>
                      {warning.message}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card className="gap-4">
            <CardHeader>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-1">
                  <CardTitle>Manual Additions</CardTitle>
                  <CardDescription>
                    Add ingredients that are not part of the recipe-driven list. Manual items stay in place when the week is regenerated.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-xl">
                <SearchableDropdown<Ingredient>
                  placeholder="Search ingredients to add directly..."
                  onSearch={searchIngredients}
                  onSelect={handleAddManualItem}
                  renderItem={(ingredient) => (
                    <div className="flex w-full items-center justify-between gap-3">
                      <span>{ingredient.name}</span>
                      <span className="text-xs text-muted-foreground">{ingredient.defaultUnit}</span>
                    </div>
                  )}
                  getItemKey={(ingredient) => ingredient.id}
                  disabled={saving || generating}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Selecting an ingredient adds it straight to the unsaved procurement list with a default quantity of 1.
              </p>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Weekly List</h2>
              <p className="text-sm text-muted-foreground">
                Changes save automatically when you leave a row. Use Save changes to retry anything that failed.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={viewMode === 'vendor' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('vendor')}
              >
                Vendor view
              </Button>
              <Button
                variant={viewMode === 'category' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('category')}
              >
                Ingredient category view
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAllBreakdownsExpanded((prev) => (prev === true ? null : true))}
              >
                <List className="mr-1.5 h-4 w-4" />
                {allBreakdownsExpanded ? 'Hide breakdowns' : 'Show breakdowns'}
              </Button>
            </div>
          </div>

          {displayItems.length === 0 ? (
            <Card className="p-12 text-center">
              <CardHeader className="px-0">
                <CardTitle>No procurement items</CardTitle>
                <CardDescription>
                  This week currently has no recipe quantities or manual additions.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="space-y-4">
              {groupedItems.map((group) => (
                <ProcurementGroupTable
                  key={group.key}
                  group={group}
                  viewMode={viewMode}
                  saving={saving}
                  allBreakdownsExpanded={allBreakdownsExpanded}
                  onDraftChange={updateDraft}
                  onInventoryChange={updateInventory}
                  onToggleExcluded={toggleExcluded}
                  onVendorChange={handleVendorChange}
                  ingredientVendorsCache={ingredientVendorsCache}
                  onFetchIngredientVendors={fetchAndCacheIngredientVendors}
                  regenDiff={regenDiff}
                  rowSaveStatus={rowSaveStatus}
                  onRowBlur={flushRowSave}
                />
              ))}
            </div>
          )}
        </>
      )}
      <QuantityProblemsDialog problems={quantityProblems} onClose={() => setQuantityProblems(null)} />
      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        weekStartDate={weekStartDate}
        weekLabel={`${formatLongDate(weekStartDate)} – ${formatLongDate(weekEndDate)}`}
        existingItems={importExistingItems}
        onConfirm={handleImportConfirm}
      />
    </div>
  );
}
